import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';
import { fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { BLUE, BLUE_GRADIENT, compactNumber } from './brand';

/**
 * One channel card (the channels directory, “my channels”, subscriptions).
 *
 *   · ownerView → the delete button (it is one of *my* channels, max 3)
 *   · otherwise → subscribe (“ជាវ”) / subscribed / waiting for approval
 */
export default function ChannelCard({ channel, ownerView = false, onSubscribe, onDelete }) {
  const { t } = useLanguage();
  const url = `/channel/${channel.slug || channel.id}`;
  const waiting = channel.pending;
  const joined = channel.subscribed;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden anim-fade-up">
      <Link to={url} className="block">
        <span
          className="block h-16"
          style={{ background: channel.cover ? `url(${fullUrl(channel.cover)}) center/cover` : BLUE_GRADIENT }}
        />
      </Link>
      <div className="px-4 pb-3 -mt-6">
        <Link to={url} className="inline-block">
          {channel.avatar ? (
            <img
              src={fullUrl(channel.avatar)}
              alt={channel.name}
              className="w-12 h-12 rounded-xl object-cover ring-2 ring-white dark:ring-gray-900"
            />
          ) : (
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold ring-2 ring-white dark:ring-gray-900"
              style={{ background: BLUE }}
            >
              {(channel.name || 'C').slice(0, 1).toUpperCase()}
            </span>
          )}
        </Link>
        <Link to={url} className="mt-2 block font-extrabold text-sm truncate hover:underline">
          {channel.name}
        </Link>
        <p className="text-[11px] text-gray-500 truncate">
          @{channel.nickname || channel.slug} · {compactNumber(channel.subscriber_count)} {t('subscribers')}
          {' · '}{compactNumber(channel.post_count)} {t('articles')}
        </p>
        {channel.about && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{channel.about.slice(0, 120)}</p>
        )}

        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${channel.is_public ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
            {channel.is_public ? t('channelPublic') : t('channelPrivate')}
          </span>
          {!channel.is_open && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
              {t('channelApproval')}
            </span>
          )}

          {ownerView ? (
            <button
              type="button"
              onClick={() => onDelete && onDelete(channel)}
              className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:underline"
            >
              <FiTrash2 className="w-3.5 h-3.5" /> {t('delete')}
            </button>
          ) : channel.role === 'owner' ? (
            <span className="ml-auto text-[10px] font-bold text-gray-400">{t('subscribeOwnChannel')}</span>
          ) : (
            <button
              type="button"
              onClick={() => onSubscribe && onSubscribe(channel)}
              className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                joined || waiting ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300' : 'text-white'
              }`}
              style={joined || waiting ? undefined : { background: BLUE }}
            >
              {joined ? <FiCheck className="w-3.5 h-3.5" /> : <FiPlus className="w-3.5 h-3.5" />}
              {joined ? t('subscribed') : waiting ? t('subscriberPending') : t('subscribe')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}